import { createFileRoute } from "@tanstack/react-router"
import {
  Container,
  Title,
  Grid,
  Card,
  Text,
  Badge,
  TextInput,
  Group,
  Button,
  Box,
  Stack,
  Paper,
  Select,
  Tabs,
  useMantineColorScheme,
} from "@mantine/core"
import {
  IconSearch,
  IconDownload,
  IconCalendar,
  IconFileText,
  IconExternalLink,
  IconShieldCheck,
  IconClock,
} from "@tabler/icons-react"
import { documentsData } from "@/data/documents-data"
import { DocumentDisclaimer } from "@/components/documents/DocumentDisclaimer"
import { useState, useMemo } from "react"
import { SEO } from "@/components/seo/SEO"
import { StructuredData, createDataCatalogSchema, createDocumentSchema } from "@/components/seo/StructuredData"
import { PageCTASection } from "@/components/ui/PageCTASection"

export const Route = createFileRoute("/documents")({
  component: DocumentsPage,
})

function DocumentsPage() {
  const [searchQuery, setSearchQuery] = useState("")
  const [categoryFilter, setCategoryFilter] = useState<string>("all")
  const [activeTab, setActiveTab] = useState<string>("all")
  const { colorScheme } = useMantineColorScheme()

  const filteredDocuments = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()
    const year = activeTab !== "all" ? parseInt(activeTab) : null

    return (
      [...documentsData]
        // Filter by search query
        .filter((doc) => {
          if (!query) return true
          return (
            doc.title.toLowerCase().includes(query) ||
            doc.description?.toLowerCase().includes(query) ||
            doc.category?.toLowerCase().includes(query) ||
            doc.tags?.some((tag) => tag.toLowerCase().includes(query))
          )
        })
        // Filter by category
        .filter((doc) => {
          if (categoryFilter === "all") return true
          return doc.category === categoryFilter
        })
        // Filter by time period
        .filter((doc) => {
          if (!year) return true
          if (!doc.date) return false
          const docYear = new Date(doc.date).getFullYear()
          return docYear >= year && docYear < year + 10
        })
        // Sort by date (newest first)
        .sort((a, b) => {
          if (!a.date && !b.date) return 0
          if (!a.date) return 1
          if (!b.date) return -1
          return new Date(b.date).getTime() - new Date(a.date).getTime()
        })
    )
  }, [searchQuery, categoryFilter, activeTab])

  const categories = Array.from(new Set(documentsData.map((doc) => doc.category).filter(Boolean))) as string[]

  // Create schemas for all documents (limited to top 50 for performance)
  const documentSchemas = useMemo(
    () => filteredDocuments.slice(0, 50).map((doc) => createDocumentSchema(doc)),
    [filteredDocuments],
  )

  return (
    <>
      <SEO
        title="Document Archive"
        description="Comprehensive collection of official court documents, depositions, legal filings, and evidence related to the Jeffrey Epstein case. All materials are publicly available with verification status clearly marked."
        canonical="https://rtefn.com/documents"
        keywords="Epstein court documents, legal filings, depositions, evidence, court records, verified documents, Epstein case files, official documents, legal records, public documents"
        ogImage="https://rtefn.com/og-default.png"
        ogImageAlt="Epstein Case Document Archive - Official Court Documents and Legal Filings"
      />
      <StructuredData data={createDataCatalogSchema(documentSchemas)} />
      <Box
        className={`min-h-screen ${
          colorScheme === "dark" ? "hero-dark" : "bg-gradient-to-br from-gray-50 via-white to-blue-50"
        }`}
        py="xl"
      >
        {colorScheme === "dark" && <Box className="noise-overlay" />}
        <Container size="xl" className="relative z-10">
          <Stack gap="xl">
            {/* Page Header */}
            <Stack ta="center" gap="md">
              <Group justify="center" gap="sm" mb="md">
                <IconFileText size={32} style={{ color: "var(--mantine-primary-color-filled)" }} />
                <Title order={1} size="h1">
                  Document Archive
                </Title>
              </Group>
              <Text size="2xl" maw={896} mx="auto" c="dimmed" lh={1.6}>
                Comprehensive collection of official court documents, depositions, legal filings, and evidence related
                to the Jeffrey Epstein case
              </Text>

              {/* Search Bar */}
              <Box maw={700} mx="auto" w="100%">
                <TextInput
                  placeholder="Search documents by title, category, or keyword..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.currentTarget.value)}
                  leftSection={<IconSearch size={16} />}
                  size="lg"
                  radius="md"
                />
              </Box>
            </Stack>

            {/* Document Disclaimer */}
            <DocumentDisclaimer />

            {/* Filters */}
            <Paper p="lg" radius="md">
              <Stack gap="md">
                {/* Time Period Tabs */}
                <Tabs value={activeTab} onChange={(value) => setActiveTab(value || "all")}>
                  <Tabs.List>
                    <Tabs.Tab value="all">All Documents</Tabs.Tab>
                    <Tabs.Tab value="2020">Recent (2020s)</Tabs.Tab>
                    <Tabs.Tab value="2010">2010s</Tabs.Tab>
                    <Tabs.Tab value="2000">2000s</Tabs.Tab>
                  </Tabs.List>
                </Tabs>

                {/* Category Filter */}
                <Group>
                  <Select
                    placeholder="Filter by category"
                    value={categoryFilter}
                    onChange={(value) => setCategoryFilter(value || "all")}
                    data={[
                      { value: "all", label: "All Categories" },
                      ...categories.map((cat) => ({ value: cat, label: cat })),
                    ]}
                  />
                  <Text size="base" c="dimmed">
                    Showing {filteredDocuments.length} of {documentsData.length} documents
                  </Text>
                </Group>
              </Stack>
            </Paper>

            {/* Document Grid */}
            {filteredDocuments.length === 0 ? (
              <Paper p="xl" radius="lg">
                <Stack gap="md" ta="center" align="center">
                  <IconSearch size={48} style={{ color: "var(--mantine-color-dimmed)" }} />
                  <Title order={3}>{searchQuery ? "No documents found" : "No documents available"}</Title>
                  <Text c="dimmed">
                    {searchQuery
                      ? "Try adjusting your search terms or filters to see more results."
                      : "The document archive is being populated with publicly available court filings and legal documents."}
                  </Text>
                </Stack>
              </Paper>
            ) : (
              <Grid gutter="lg">
                {filteredDocuments.map((doc, index) => (
                  <Grid.Col key={index} span={{ base: 12, md: 6, lg: 4 }}>
                    <Card
                      shadow="sm"
                      padding="lg"
                      radius="lg"
                      className="h-full transition-all duration-200 hover:shadow-lg"
                      style={{
                        background: colorScheme === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(255, 255, 255, 0.95)",
                        border:
                          colorScheme === "dark"
                            ? "1px solid rgba(255, 255, 255, 0.1)"
                            : "1px solid rgba(0, 0, 0, 0.1)",
                        backdropFilter: "blur(20px)",
                      }}
                    >
                      <Stack gap="md" className="h-full">
                        {/* Header with badges */}
                        <Group justify="space-between" align="flex-start" wrap="nowrap">
                          <Stack gap="xs" className="flex-1">
                            <Group gap="xs">
                              {doc.category && (
                                <Badge color="blue" variant="light" size="sm">
                                  {doc.category}
                                </Badge>
                              )}
                              {doc.is_verified && (
                                <Badge color="green" variant="light" size="sm">
                                  <Group gap={4}>
                                    <IconShieldCheck size={12} />
                                    Verified
                                  </Group>
                                </Badge>
                              )}
                              {doc.r2_url && (
                                <Badge color="cyan" variant="light" size="sm">
                                  Archived
                                </Badge>
                              )}
                            </Group>
                            {/* Enhanced verification info */}
                            <Group gap="xs">
                              {doc.source_tier && (
                                <Badge
                                  color={doc.source_tier === 1 ? "green" : doc.source_tier === 2 ? "blue" : "yellow"}
                                  variant="dot"
                                  size="xs"
                                >
                                  Tier {doc.source_tier} Source
                                </Badge>
                              )}
                              {doc.verification_method && (
                                <Badge color="gray" variant="outline" size="xs">
                                  {doc.verification_method}
                                </Badge>
                              )}
                              {doc.case_number && (
                                <Badge color="violet" variant="outline" size="xs">
                                  Case #{doc.case_number}
                                </Badge>
                              )}
                            </Group>
                          </Stack>
                          {doc.date && (
                            <Group gap="xs" className="shrink-0">
                              <IconCalendar size={14} />
                              <Text size="xs" c="dimmed">
                                {new Date(doc.date).toLocaleDateString()}
                              </Text>
                            </Group>
                          )}
                        </Group>

                        {/* Title */}
                        <Title order={3} className="line-clamp-2" size="1.25rem" style={{ flex: "0 0 auto" }}>
                          {doc.title}
                        </Title>

                        {/* Description */}
                        {doc.description && (
                          <Text size="base" className="line-clamp-3 flex-1" c="dimmed" lh={1.6}>
                            {doc.description}
                          </Text>
                        )}

                        {/* Tags */}
                        {doc.tags && doc.tags.length > 0 && (
                          <Group gap="xs" className="flex-wrap">
                            {doc.tags.slice(0, 3).map((tag, tagIndex) => (
                              <Badge key={tagIndex} variant="outline" size="xs" color="gray">
                                {tag}
                              </Badge>
                            ))}
                            {doc.tags.length > 3 && (
                              <Badge variant="outline" size="xs" color="gray">
                                +{doc.tags.length - 3} more
                              </Badge>
                            )}
                          </Group>
                        )}

                        {/* Actions */}
                        <Stack gap="xs" style={{ marginTop: "auto" }}>
                          <Group justify="space-between">
                            {doc.r2_url ? (
                              <Button
                                leftSection={<IconDownload size={16} />}
                                variant="light"
                                color="blue"
                                size="sm"
                                component="a"
                                href={doc.r2_url}
                                target="_blank"
                                fullWidth={!doc.source_url}
                              >
                                Download Archive
                              </Button>
                            ) : doc.file_url ? (
                              <Button
                                leftSection={<IconDownload size={16} />}
                                variant="light"
                                color="blue"
                                size="sm"
                                component="a"
                                href={doc.file_url}
                                target="_blank"
                                fullWidth={!doc.source_url}
                              >
                                Download
                              </Button>
                            ) : (
                              <Button variant="light" size="sm" disabled color="gray" fullWidth={!doc.source_url}>
                                No File
                              </Button>
                            )}

                            {doc.source_url && (
                              <Button
                                leftSection={<IconExternalLink size={14} />}
                                variant="subtle"
                                size="sm"
                                component="a"
                                href={doc.source_url}
                                target="_blank"
                                color="blue"
                              >
                                Source
                              </Button>
                            )}
                          </Group>
                        </Stack>
                      </Stack>
                    </Card>
                  </Grid.Col>
                ))}
              </Grid>
            )}

            {/* Document Summary */}
            <Paper p="lg" radius="md">
              <Group justify="space-around">
                <Stack ta="center" gap="xs">
                  <Text size="2xl" fw="bold">
                    {documentsData.filter((d) => d.is_verified).length}
                  </Text>
                  <Text size="base" c="green">
                    Verified Documents
                  </Text>
                </Stack>
                <Stack ta="center" gap="xs">
                  <Text size="2xl" fw="bold">
                    {categories.length}
                  </Text>
                  <Text size="base" c="blue">
                    Document Categories
                  </Text>
                </Stack>
                <Stack ta="center" gap="xs">
                  <Text size="2xl" fw="bold">
                    {documentsData.length}
                  </Text>
                  <Text size="base" c="orange">
                    Total Documents
                  </Text>
                </Stack>
                <Stack ta="center" gap="xs">
                  <Text size="2xl" fw="bold">
                    2025
                  </Text>
                  <Text size="sm" c="dimmed">
                    Most Recent
                  </Text>
                </Stack>
              </Group>
            </Paper>

            {/* Call to Action */}
            <PageCTASection contextualAction={{ href: "/timeline", label: "View Timeline", icon: IconClock }} />
          </Stack>
        </Container>
      </Box>
    </>
  )
}
